import React from "react";
import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper, LabelString, ButtonIcon, ICONS, LabelDollarFormatted, LabelPercentFormatted } from "@loanbossinc/component-library";
import { defaultField, defaultPercentField, defaultBooleanField, subStringDollarFormat } from "helpers/row-logic-helper";
import styled from "styled-components";

import { shouldShowField } from "../loanDetailEditUtil";

import { hasRecourseSchedule, generateDetailsConfig, shouldSendNotification } from "./section-loan-guarantor-rows-logic";

const StyledPill = styled.span`&&{
  padding-left: 11px;
  width: 71px;
  height: 28px;
  font-family: Work Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  text-align: center;
  text-transform: uppercase;  
  color:#FFFFFF;
  background: #0F9AD6;
  border-radius: 4px;
}`;
const StyledLabelDollarFormatted = styled(LabelDollarFormatted)`&&{
  float: left;
}`;
const StyledLabelPercentFormatted = styled(LabelPercentFormatted)`&&{
  float: left;
}`;
const StyledNoData = styled.span`&&{
  float: left;
}`;
const StyledButtonWrapper = styled.div`&&{  
  display: flex;
  float: right;
}`;

const DEFAULT_WIDTH = 138;

export const rows = (expandRow, loan, index, schedulesIndex, startSave) => ({
  netWorth: {
    isVisible: data => shouldShowField(data, "netWorth"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Net Worth",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "netWorth"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  liquidity: {
    isVisible: data => shouldShowField(data, "liquidity"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Liquidity",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => defaultField(data, "liquidity"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  leverage: {
    isVisible: data => shouldShowField(data, "leverage"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Leverage",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "leverage", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      }
    ]
  },
  recourseAmount: {
    isVisible: data => !hasRecourseSchedule(data) && shouldShowField(data, "recourseAmount"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Recourse Amount",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DOLLAR,
        map: data => subStringDollarFormat(data, "recourseAmount"),
        Cell: tableSideColumnMapper,
        decimals: 3

      }
    ]
  },
  recoursePercent: {
    isVisible: data => !hasRecourseSchedule(data) && shouldShowField(data, "recoursePercent"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Recourse Percent",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "recoursePercent", 100),
        Cell: tableSideColumnMapper,
        decimals: 3

      }
    ]
  },

  burndown: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Burndown",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.BOOLEAN,
        map: data => defaultBooleanField(data, "burndownRaw"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  notes: {
    isVisible: data => shouldShowField(data, "notes"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Notes",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT,
        map: data => defaultField(data, "notes"),
        Cell: tableSideColumnMapper
      }
    ]
  },
  hasRecourseSchedule: {
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Recourse Schedule",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.BOOLEAN,
        map: data => defaultBooleanField(data, "hasRecourseSchedule"),
        Cell: tableSideColumnMapper
      }
    ]
  },
  reference: {
    isVisible: data => shouldShowField(data, "reference"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Reference",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT,
        map: data => defaultField(data, "reference"),
        Cell: tableSideColumnMapper
      }
    ]
  },
  recourseSchedule: {
    isVisible: data => hasRecourseSchedule(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.TABLE,
        colSpan: 2,
        Cell: tableSideColumnMapper,
        columns: [
          {
            id: "recourseObligation",
            label: "Recourse obligation",
            align: "center",
            alignHeader: "left",
            Cell: (value, isSelected) => {
              switch (value.recourseType) {
                case "recourseAmountRadio":
                  return (<StyledLabelDollarFormatted value={value.recourseAmount} decimals={2} lines={1} isSelected={isSelected} />);
                case "recoursePercentRadio":
                  return (<StyledLabelPercentFormatted value={value.recoursePercent / 100} decimals={2} lines={1} isSelected={isSelected} />);
                default:
                  return (<StyledNoData>--</StyledNoData>);
              }
            }
          },
          {
            id: "dscr",
            label: "DSCR",
            path: "dscr",
            alignHeader: "center",
            Cell: (value, isSelected) => {
              if (Boolean(value) && value !== "--") {
                value = `${value}x`;
              }

              return (<LabelString value={value} isSelected={isSelected} />);
            }
          },
          {
            id: "ltv",
            label: "LTV",
            path: "ltv",
            alignHeader: "center",
            Cell: (value, isSelected) => {
              if (Boolean(value) && value !== "--") {
                value = `${value}%`;
              }

              return (<LabelString value={value} isSelected={isSelected} />);
            }
          },
          {
            id: "button",
            path: "",
            alignHeader: "center",
            width: "25%",
            Cell: (value, isSelected) => (<StyledButtonWrapper>
              {value.isCurrent && (<StyledPill>CURRENT</StyledPill>)}
              <ButtonIcon onClick={() => expandRow(value, index, "personalGuarantors")} iconName={value.isDetailsExpanded ? ICONS.UNFOLD_LESS : ICONS.UNFOLD_MORE} />
                                          </StyledButtonWrapper>)
          }
        ],
        itemsRootPath: "guarantorRecourseSchedules",
        map: data => data.guarantorRecourseSchedules,
        noDataMessage: "No Deals Found",
        hideHeader: false,
        addDropShodow: false,
        detailsConfig: generateDetailsConfig(loan, index, schedulesIndex, startSave)
      }
    ]
  },
  burnup: {
    isVisible: data => hasRecourseSchedule(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Burnup",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.BOOLEAN,
        map: data => defaultBooleanField(data, "burnup"),
        Cell: tableSideColumnMapper
      }
    ]
  },

  shouldSendNotification: {
    isVisible: data => shouldSendNotification(data),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "notification contacts",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT,
        map: data => {
          const contacts = defaultField(data, "guarantorRecourseExposureNotificationContacts");

          const list = contacts.filter(x => x.contacts).sort((a, b) => (`${a.contacts.fullName}`).localeCompare(b.contacts.fullName)).map(x => (<p>{x.contacts.fullName}</p>));
          if (list.length > 0) {
            return list;
          }

          return [];
        },
        Cell: tableSideColumnMapper
      }
    ]
  }
});
